/**
 * Gemini 3.1 Flash TTS — server-side proxy only (no browser API key).
 * Model id matches functions/src/voice/geminiTts.js default.
 */

import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import { GEMINI_LIVE_DEFAULT_VOICE } from './geminiLiveVoices';
import {
  TTS_PLAYBACK_POST_END_PAD_MS,
  TTS_AUDIO_TAIL_EPSILON_SEC,
  TTS_PREMATURE_TAIL_POLL_MS,
  TTS_PREMATURE_TAIL_MAX_EXTRA_MS,
  playPrefetchedAudio,
  stopSpeaking as stopSharedPlayback,
} from '../ttsService';

const DEFAULT_VOICE = GEMINI_LIVE_DEFAULT_VOICE || 'Aoede';

/** Must match GEMINI_TTS_MODEL_DEFAULT in functions/src/voice/geminiTts.js */
const GEMINI_FLASH_TTS_MODEL = 'gemini-3.1-flash-tts-preview';

/**
 * @typedef {Object} GeminiFlashTTSConfig
 * @property {string} [voiceName]
 */

class GeminiFlashTTSService {
  constructor() {
    /** @type {GeminiFlashTTSConfig} */
    this.config = {
      voiceName: DEFAULT_VOICE,
    };
    this.currentAudio = null;
    this._isSpeaking = false;
    this.onStart = null;
    this.onEnd = null;
    this.onError = null;
    this._postEndPadTimer = null;
    this._completePostEndPlay = null;
    this._tailRecoveryPollId = null;
    this._playbackToken = 0;
  }

  _clearTailRecoveryPoll() {
    if (this._tailRecoveryPollId !== null) {
      clearInterval(this._tailRecoveryPollId);
      this._tailRecoveryPollId = null;
    }
  }

  /**
   * @param {GeminiFlashTTSConfig} config
   */
  configure(config = {}) {
    this.config = {
      ...this.config,
      ...config,
      voiceName:
        typeof config.voiceName === 'string' && config.voiceName.trim()
          ? config.voiceName.trim()
          : this.config.voiceName || DEFAULT_VOICE,
    };
  }

  /**
   * @param {{ onStart?: function, onEnd?: function, onError?: function }} handlers
   */
  setHandlers(handlers) {
    if (handlers.onStart) this.onStart = handlers.onStart;
    if (handlers.onEnd) this.onEnd = handlers.onEnd;
    if (handlers.onError) this.onError = handlers.onError;
  }

  /**
   * @param {string} text
   * @param {Object} [options]
   * @param {string} [options.voiceName] - Override prebuilt voice for this request
   * @returns {Promise<string>} Blob URL
   */
  async generateSpeech(text, options = {}) {
    if (!text || typeof text !== 'string' || text.trim().length === 0) {
      throw new Error('Text cannot be empty');
    }

    const voiceName =
      (typeof options.voiceName === 'string' && options.voiceName.trim()
        ? options.voiceName.trim()
        : null) || this.config.voiceName || DEFAULT_VOICE;

    const callable = firebase
      .app()
      .functions('us-central1')
      .httpsCallable('geminiFlashTTSProxy');
    const result = await callable({
      text: text.trim(),
      voiceName,
      model: GEMINI_FLASH_TTS_MODEL,
    });
    const b64 = result?.data?.audioBase64;
    if (!b64 || typeof b64 !== 'string') {
      throw new Error('Gemini TTS: No audio in response');
    }
    const binaryString = atob(b64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'audio/wav' });
    return URL.createObjectURL(blob);
  }

  /**
   * Generate then play via the shared iOS-unlocked Audio path when possible.
   * @param {string} text
   * @param {Object} [options]
   * @returns {Promise<void>}
   */
  async speak(text, options = {}) {
    if (this._isSpeaking) {
      this.stop();
    }

    const onStartCb = options.onStart ?? this.onStart;
    const onEndCb = options.onEnd ?? this.onEnd;
    const onErrorCb = options.onError ?? this.onError;

    try {
      const blobUrl = await this.generateSpeech(text, options);
      const res = await fetch(blobUrl);
      const blob = await res.blob();
      try {
        URL.revokeObjectURL(blobUrl);
      } catch {
        // ignore
      }
      this._isSpeaking = true;
      await playPrefetchedAudio(blob, {
        onStart: () => {
          this._isSpeaking = true;
          onStartCb?.();
        },
        onEnd: () => {
          this._isSpeaking = false;
          onEndCb?.();
        },
        onError: (err) => {
          this._isSpeaking = false;
          onErrorCb?.(err);
        },
      });
      this._isSpeaking = false;
    } catch (error) {
      console.error('[GeminiFlashTTS] speak() error:', error);
      this._isSpeaking = false;
      onErrorCb?.(error);
      throw error;
    }
  }

  stop() {
    this._playbackToken += 1;
    this._clearTailRecoveryPoll();
    if (this._postEndPadTimer) {
      clearTimeout(this._postEndPadTimer);
      this._postEndPadTimer = null;
    }
    if (typeof this._completePostEndPlay === 'function') {
      const finish = this._completePostEndPlay;
      this._completePostEndPlay = null;
      finish();
    }
    try {
      stopSharedPlayback();
    } catch {
      // ignore
    }
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    this._isSpeaking = false;
  }

  /** @returns {boolean} */
  speaking() {
    return this._isSpeaking;
  }

  /** @returns {boolean} */
  get isSpeaking() {
    return this._isSpeaking;
  }
}

export const geminiFlashTTSService = new GeminiFlashTTSService();

export { GeminiFlashTTSService, GEMINI_FLASH_TTS_MODEL };

export default geminiFlashTTSService;
